import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideServiceWorker } from '@angular/service-worker';
import { provideRouter } from '@angular/router';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { provideAppCheck, initializeAppCheck, ReCaptchaEnterpriseProvider } from '@angular/fire/app-check';
import {
  provideFirestore,
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
} from '@angular/fire/firestore';
import { provideStorage, getStorage, connectStorageEmulator } from '@angular/fire/storage';
import { provideAuth, getAuth, connectAuthEmulator } from '@angular/fire/auth';
// setPersistence/browserSessionPersistence specifically break when imported
// from @angular/fire/auth in an esbuild production build — "Class
// constructor _BrowserSessionPersistence cannot be invoked without 'new'"
// (angular/angularfire#3550, reproduced against this exact build). Every
// other Auth import above is unaffected; only these two need the raw
// firebase/auth import instead.
import { browserSessionPersistence, setPersistence } from 'firebase/auth';
import { provideFunctions, getFunctions, connectFunctionsEmulator } from '@angular/fire/functions';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { isConfigured } from './shared/models/environment.model';

// Every provider below follows the same shape: create the real Firebase
// service, then — only in local dev — repoint it at the matching emulator
// (Developer Execution Guide Part 2.4/2.5). Nothing built against
// environment.useEmulators=true ever reaches a live project.
/** A chef's/waiter's phone — see the persistence note in provideAuth below. */
function isStaffFloorDevice(): boolean {
  if (typeof window === 'undefined') return false;
  if (/^\/(staff|kitchen|waiter)(\/|$)/.test(window.location.pathname)) return true;
  try {
    return !!window.localStorage.getItem('nv.staff.device');
  } catch {
    return false;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),

    provideFirebaseApp(() => initializeApp(environment.firebase)),

    // App Check attests that a request came from *this app* rather than from
    // a script holding the public apiKey. It matters most for the guest flow:
    // anonymous sign-in plus the claimTable callable is otherwise an open,
    // unauthenticated, billable endpoint.
    //
    // Only registered when a site key is really configured, so the emulator
    // and any build without one keep working — see environment.model.ts. The
    // REPLACE_WITH_ check is not decoration: environment.ts ships placeholders
    // for the whole prod config, and unlike the inert ones (apiKey and friends,
    // which are simply wrong until filled in) a placeholder here would actually
    // start App Check and hand reCAPTCHA a key it will reject on every call.
    ...(isConfigured(environment.appCheckSiteKey)
      ? [
          provideAppCheck(() =>
            initializeAppCheck(undefined, {
              provider: new ReCaptchaEnterpriseProvider(environment.appCheckSiteKey as string),
              // Lets a signed-in session refresh its App Check token in the
              // background instead of failing once the first one expires.
              isTokenAutoRefreshEnabled: true,
            })
          ),
        ]
      : []),

    provideFirestore(() => {
      // ignoreUndefinedProperties: every write site in this codebase spreads
      // a form's raw value straight into setDoc()/updateDoc() (e.g.
      // { ...input, createdBy, createdAt: ... }), and a form field left
      // blank routinely produces `field: undefined` in that spread rather
      // than the key being absent. Firestore's default behaviour rejects
      // that outright — "Unsupported field value: undefined" — which is not
      // a validation message a form can act on; it's a hard failure that
      // leaves the dialog stuck open with a raw SDK error in it. Found
      // independently in three unrelated dialogs (task edit/create with an
      // empty Description, a purchase request with empty Notes) during E2E
      // work — the same defect, not three different ones, so it's fixed
      // once here rather than patched at every call site (and every one
      // written afterward). The trade this setting makes — undefined values
      // are silently dropped instead of throwing — is exactly what every
      // one of those call sites already wants: "don't touch this field",
      // never "error out because the guest left a note blank".
      // persistentLocalCache: the POS runs on venue wifi that drops mid-
      // service. Without an on-disk cache the whole shell goes blank the
      // moment the connection does — no menu, no open orders, no bill to
      // settle. With it, every document the app has already read stays
      // readable and queryable offline, and reads/writes reconcile on
      // reconnect. persistentMultipleTabManager keeps that cache coherent
      // across the two or three tabs a busy counter keeps open (kitchen
      // board + billing + floor) instead of the last tab silently winning
      // the single-tab lock. Callable-backed writes still need their own
      // offline queue — that is prod-readiness 4.5/4.6, layered on top.
      const firestore = initializeFirestore(getApp(), {
        ignoreUndefinedProperties: true,
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
      });
      if (environment.useEmulators) {
        connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
      }
      return firestore;
    }),

    provideAuth(() => {
      const auth = getAuth();
      if (environment.useEmulators) {
        connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      }
      // Firebase Auth's default persistence (IndexedDB, falling back to
      // localStorage) is shared across *every* tab of the same origin: it
      // exists to let signing out in one tab sign a person out everywhere.
      // This app has no use for that and a real cost from it — a guest
      // opening a table's QR link in one tab calls signInAnonymously(),
      // which is a sign-in like any other, and it silently replaced a
      // signed-in Admin's session in a completely different tab of the same
      // browser. Session persistence keys the signed-in user off
      // sessionStorage instead, which is per-tab, so a guest tab and a staff
      // tab in the same browser never see or affect each other's session.
      // The trade is that staff sign in again after closing a tab rather
      // than staying signed in indefinitely — acceptable for a staff app,
      // and consistent with how deliberately this codebase already treats
      // sessions (see revokeRefreshTokens on role change/deletion).
      //
      // setPersistence() is async. Firing it here without awaiting left a
      // real gap: any sign-in that ran before it resolved — a guest's
      // signInAnonymously in WaiterCall, an owner's signInWithEmailAndPassword
      // in Login, both of which can fire within milliseconds of bootstrap —
      // landed on the still-default IndexedDB persistence instead, which is
      // exactly the cross-tab leak this exists to prevent: a guest tab
      // signing in anonymously would broadcast into a staff tab open in the
      // same browser and silently sign them out.
      //
      // Every actual sign-in call site now explicitly awaits setPersistence
      // itself, immediately before signing in — see GuestRequestService.
      // ensureAnonymousSession, Login.submit and Impersonate.ngOnInit. This
      // call stays here too (harmless and idempotent) so persistence is
      // already switched before that first await in the common case.
      //
      // A blocking app-wide initializer (provideAppInitializer) was tried
      // instead of per-call-site awaits and reliably crashed the app in the
      // production build: setPersistence/browserSessionPersistence break
      // when imported from @angular/fire/auth under esbuild's production
      // bundling specifically (angular/angularfire#3550 — "Class constructor
      // _BrowserSessionPersistence cannot be invoked without 'new'"), so
      // *every* call failed, and failing inside a blocking initializer takes
      // the whole app down instead of just this one switch. Importing
      // setPersistence/browserSessionPersistence from firebase/auth directly
      // (below) is the actual fix; every other Auth import above is
      // unaffected and stays on @angular/fire/auth.
      //
      // The one exception: a chef's or waiter's own phone. They sign in with a
      // PIN and are meant to stay signed in (features/staff-floor signs in on
      // local persistence), and switching to session persistence here would
      // move their user into sessionStorage and sign them out the moment the
      // browser closes. A phone that has signed a PIN person in remembers it
      // (nv.staff.device); on one of those, or on the staff screens
      // themselves, leave persistence alone.
      if (!isStaffFloorDevice()) void setPersistence(auth, browserSessionPersistence);
      return auth;
    }),

    provideStorage(() => {
      const storage = getStorage();
      if (environment.useEmulators) {
        connectStorageEmulator(storage, '127.0.0.1', 9199);
      }
      return storage;
    }),

    provideFunctions(() => {
      // Explicit region: getFunctions() defaults to us-central1, but the
      // functions are pinned next to Firestore in asia-south1 (see
      // functions/src/index.ts). Getting this wrong surfaces as a CORS error
      // on every callable, not as a missing-function error.
      const functions = getFunctions(getApp(), environment.functionsRegion);
      if (environment.useEmulators) {
        connectFunctionsEmulator(functions, '127.0.0.1', 5001);
      }
      return functions;
    }),

    // provideDatabase is deliberately absent: nothing in src/ calls it, and
    // it would fail outright against a live project because no databaseURL
    // is configured. Add it back beside its first real use, not before —
    // provideStorage above is exactly that principle applied the other way,
    // added once LogoUpload (branding-settings) became its first caller.

    // Caches the app shell (JS/CSS/index.html) so the POS loads with zero
    // network, not just after it's already running (prod-readiness 4.2 —
    // persistentLocalCache, 4.1, only helps once the app's JS is executing).
    // `ngsw-worker.js` is generated only by the `production` build
    // configuration (angular.json), so this is a harmless no-op file-not-
    // found in dev/emulator builds even without the `enabled` guard; the
    // guard is belt-and-braces so a stray production-config emulator run
    // never registers it either. registerWhenStable defers registration
    // until 30s after bootstrap so it never competes with the app's own
    // startup network requests. Coexistence with firebase-messaging-sw.js:
    // see notification.service.ts's registration, which now uses a
    // non-root scope so the two workers don't fight over '/'.
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production && !environment.useEmulators,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ]
};
